import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  clearSession,
  getStoredOperator,
  getToken,
  onUnauthorized,
  setSession,
} from "../api/client";
import { auth } from "../api/endpoints";
import type { OperatorOut } from "../api/types";

interface AuthState {
  operator: OperatorOut | null;
  /** True until the stored token has been checked against the server. */
  checking: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [operator, setOperator] = useState<OperatorOut | null>(() =>
    getToken() ? getStoredOperator<OperatorOut>() : null,
  );
  const [checking, setChecking] = useState<boolean>(() => getToken() !== null);

  // A stored token survives a reload, but the server keeps its sessions in
  // memory — restarting the API invalidates every token. So the stored one is
  // treated as a guess until /auth/me confirms it, which avoids showing a
  // signed-in shell that then fails every request.
  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    auth
      .me()
      .then((current) => {
        if (!cancelled) setOperator(current);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Only a confirmed 401 means the token itself is invalid. Anything
        // else here -- most commonly ApiError.status === 0, client.ts's own
        // signal for "the request never reached the server" -- is a
        // transport problem, not proof the session is bad. Logging the user
        // out over a dropped connection (flaky mobile data, the API
        // restarting at the exact moment the page loaded) would cost them
        // their session for no reason; the stored token stays, and a later
        // genuinely-invalid token is still caught by the normal request flow
        // via onUnauthorized below.
        if (error instanceof ApiError && error.status === 401) {
          clearSession();
          setOperator(null);
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Any later 401 (expired session, API restarted mid-session) drops us back
  // to the sign-in screen rather than leaving broken pages on screen.
  useEffect(() => onUnauthorized(() => setOperator(null)), []);

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await auth.login(email, password);
    setSession(response.token, response.operator);
    setOperator(response.operator);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      // The token may already be invalid server-side; either way this session
      // is over locally.
    }
    clearSession();
    setOperator(null);
  }, []);

  const value = useMemo(
    () => ({ operator, checking, signIn, signOut }),
    [operator, checking, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}
